import { HTMLAttributes, useEffect, useRef } from "react";
import { Interpolation, Theme, css, useTheme } from "@emotion/react";

export function HorizontalPicker<T>({
  css: cssProp,
  items,
  value,
  onChange,
  ...props
}: Omit<HTMLAttributes<HTMLDivElement>, "onChange"> & {
  css?: Interpolation<Theme>;
  items: Array<{ label: string; value: T }>;
  value: T;
  onChange: (value: T) => void;
}) {
  const theme = useTheme();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current) {
      const children = Array.from(ref.current.children);

      const index = items.findIndex((item) => item.value === value);

      if (index === -1) {
        return;
      }

      const target = children[index];

      const isMobile = ref.current.clientWidth <= 768;

      if (target instanceof HTMLElement) {
        ref.current.scrollTo({
          left: target.offsetLeft - (isMobile ? 16 : 24),
          behavior: "smooth",
        });
      }
    }
  }, [value]);

  return (
    <div
      css={css`
        position: relative;
        margin: 0 calc(-1 * var(--page-padding));
      `}
      {...props}
    >
      <div
        ref={ref}
        css={css`
          display: flex;
          align-items: center;
          padding: 0 var(--page-padding);
          overflow: auto;
        `}
      >
        {items.map((item) => {
          const focused = item.value === value;

          return (
            <div
              key={item.label}
              css={css`
                position: relative;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;
                padding: 0 8px 10px;
                color: ${focused ? theme.color.grey_12 : theme.color.grey_a};
                font-weight: ${focused ? "700" : "400"};
                cursor: pointer;
              `}
              onClick={() => {
                onChange(item.value);
              }}
            >
              {item.label}
              {focused && (
                <div
                  css={css`
                    position: absolute;
                    left: 0;
                    right: 0;
                    bottom: 0px;
                    height: 3px;
                    background-color: ${theme.color.grey_12};
                    z-index: 1;
                  `}
                />
              )}
            </div>
          );
        })}
        <div
          css={css`
            position: absolute;
            left: 0;
            right: 0;
            bottom: 0;
            height: 2px;
            background-color: ${theme.color.grey_e2};
          `}
        />
      </div>
    </div>
  );
}
